/**
 * Task screen — the crew's phone view of a task.
 *
 * Organized by intent into a top segmented control: act on the task, find files,
 * talk to the crew, update the requester, capture the recap. The app's own bottom
 * nav (AppShell) is untouched, so the task tabs live at the TOP — no second bottom
 * bar. Composes the section components from CrewTaskDetail.tsx (TimerCard,
 * SiteCard, InstructionsCard, UpdateComposer, …), which remain the single source
 * for each section's behavior.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useCrewTaskDetail } from '@/api/crew-today';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { cn } from '@/lib/cn';
import { VehicleIssueCard } from '@/components/crew/VehicleIssueCard';
import { RecapNotesCard } from '@/components/projects/RecapNotesCard';
import { TaskTimesEditor } from '@/components/projects/TaskTimesEditor';
import {
  ActivityFeed,
  BackBar,
  ChangeStatusRow,
  CrewCard,
  InstructionsCard,
  PickupLocationsCard,
  PlansCard,
  PunchCard,
  SiteCard,
  SupervisorTaskBar,
  TaskDocumentsCard,
  TaskHeader,
  TimerCard,
  UpdateComposer,
} from './CrewTaskDetail';

type TabKey = 'task' | 'files' | 'updates' | 'recap';

/** Map a notification deep-link hash to the tab it should open. Chat moved to
 *  the project (D1), so a `#chat`/`#crew-chat` hash no longer targets a task tab. */
function tabFromHash(hash: string): TabKey | null {
  const map: Record<string, TabKey> = {
    '#updates': 'updates',
    '#files': 'files',
    '#recap': 'recap',
    '#task': 'task',
  };
  return map[hash] ?? null;
}

export function CrewTaskDetailTabbed() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useCrewTaskDetail(taskId);
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const canChangeStatus = me?.role === 'crew' || canManage;
  // Chat + Recap are crew/supervisor surfaces; a PM/sales read-only viewer
  // doesn't see them (mirrors the current screen's gating).
  const canCrew = me?.role === 'crew' || canManage;
  const [editingTimes, setEditingTimes] = useState(false);
  // A notification deep link (…/tasks/:id#crew-chat) opens the matching tab.
  const [tab, setTab] = useState<TabKey>(() => tabFromHash(location.hash) ?? 'task');

  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate('/today'));

  useEffect(() => {
    const t = tabFromHash(location.hash);
    if (t) setTab(t);
  }, [location.hash]);

  // Chat moved to the project (D1) — a deep link that still targets task chat
  // (#chat / #crew-chat, e.g. an older mention email) redirects to the project's
  // chat surface rather than showing nothing here.
  useEffect(() => {
    if ((location.hash === '#chat' || location.hash === '#crew-chat') && data?.project_id) {
      navigate(`/projects/${data.project_id}#chat`, { replace: true });
    }
  }, [location.hash, data?.project_id, navigate]);

  const tabs = useMemo(
    () =>
      (
        [
          { key: 'task', label: 'Task', show: true },
          { key: 'files', label: 'Files', show: true },
          { key: 'updates', label: 'Updates', show: true },
          { key: 'recap', label: 'Recap', show: canCrew },
        ] as { key: TabKey; label: string; show: boolean }[]
      ).filter((t) => t.show),
    [canCrew],
  );

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-full bg-fill-soft">
        <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4">
          <BackBar onBack={goBack} />
          <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim mt-4">
            {(error as Error | undefined)?.message ?? 'This task could not be found.'}
          </div>
        </div>
      </div>
    );
  }

  const active = tabs.some((t) => t.key === tab) ? tab : 'task';

  return (
    <div className="min-h-full bg-fill-soft pb-10">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackBar onBack={goBack} />
        <TaskHeader data={data} />

        {/* Top segmented control — sticky so switching stays one tap away as the
            panel scrolls. Page-level nav; the app's bottom nav is separate. */}
        <div className="sticky top-0 z-10 -mx-1 px-1 pt-1 bg-fill-soft">
          <div
            role="tablist"
            aria-label="Task sections"
            className="grid rounded-xl bg-fill p-1 border border-line"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={active === t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'relative rounded-lg py-1.5 text-[13px] font-medium transition-colors',
                  active === t.key
                    ? 'bg-bg text-ink shadow-sm'
                    : 'text-ink-dim hover:text-ink',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {active === 'task' && (
          <div className="space-y-4">
            {canManage && <SupervisorTaskBar taskId={data.id} />}
            <TimerCard
              data={data}
              canAct={canChangeStatus}
              canManage={canManage}
              onAdjust={() => setEditingTimes(true)}
            />
            {editingTimes && data.started_at && data.completed_at && (
              <TaskTimesEditor
                taskId={data.id}
                startedAt={data.started_at}
                completedAt={data.completed_at}
                onClose={() => setEditingTimes(false)}
              />
            )}
            {canChangeStatus && (
              <ChangeStatusRow taskId={data.id} status={data.status} canManage={canManage} />
            )}
            {data.type === 'pickup' && data.pickup_locations.length > 0 && (
              <PickupLocationsCard data={data} />
            )}
            <SiteCard data={data} />
            <InstructionsCard data={data} />
            <VehicleIssueCard
              vehicleId={data.vehicle_id}
              vehicleLabel={data.vehicle_label}
              taskId={data.id}
            />
            {data.crew.length > 0 && <CrewCard data={data} />}
          </div>
        )}

        {active === 'files' && (
          <div className="space-y-4">
            <PlansCard projectId={data.project_id} />
            <TaskDocumentsCard projectId={data.project_id} />
          </div>
        )}

        {active === 'updates' && (
          <div className="space-y-4">
            <UpdateComposer taskId={data.id} audience="Visible to the requester" />
            <ActivityFeed taskId={data.id} timeline={data.timeline} />
          </div>
        )}

        {active === 'recap' && canCrew && (
          <div className="space-y-4">
            <RecapNotesCard
              projectId={data.project_id}
              variant="capture"
              audience="Goes in the closeout recap"
            />
            <PunchCard taskId={data.id} />
          </div>
        )}

        <button
          onClick={() => navigate(`/projects/${data.project_id}`)}
          className="flex items-center justify-between w-full bg-bg rounded-2xl border border-line p-4 text-sm text-ink-dim hover:bg-fill-soft"
        >
          <span>Open full project · {data.project_client_name}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
